import { Box, Stack, Typography, useTheme } from "@mui/material";
import { ArrowDownRight, ArrowUpRight, Lightbulb, Wallet } from "lucide-react";
import type { FC } from "react";
import { FinancialBalanceRow } from "web_myplann/Dashboard/Components/FinancialBalanceRow";
import { useFinancialBalanceHealth } from "web_myplann/Dashboard/Hooks/useFinancialBalanceHealth";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { signedNumberFormatter } from "web_myplann/Shared/Utils/numberFormatters";

const R = Resources.Dashboard.FinancialBalance;

type Props = {
	income: number;
	expense: number;
	balance: number;
	currencySign: string;
	onClick: () => void;
};

export const FinancialBalanceSummaryCard: FC<Props> = ({
	income,
	expense,
	balance,
	currencySign,
	onClick,
}) => {
	const theme = useTheme();
	const { t } = useResource();

	const { positive, health, tipText, tipCta, tipCtaColor } =
		useFinancialBalanceHealth({ income, balance, currencySign });

	return (
		<Stack
			component="button"
			onClick={onClick}
			gap={1.2}
			p={1.6}
			width="100%"
			bgcolor={theme.palette.background.paper}
			border="none"
			borderRadius={theme.radii.lg}
			sx={{
				cursor: "pointer",
				"&:active": { bgcolor: theme.palette.surface.muted },
			}}
		>
			<FinancialBalanceRow
				icon={ArrowUpRight}
				iconColor={theme.palette.green[500]}
				label={t(R.income)}
				amount={income}
				currencySign={currencySign}
				amountColor={theme.palette.text.primary}
			/>
			<FinancialBalanceRow
				icon={ArrowDownRight}
				iconColor={theme.palette.red[500]}
				label={t(R.expense)}
				amount={expense}
				currencySign={currencySign}
				amountColor={theme.palette.text.primary}
			/>
			<Box pt={1} borderTop={1} borderColor="surface.border">
				<Stack
					direction="row"
					alignItems="center"
					justifyContent="space-between"
				>
					<Stack direction="row" alignItems="center" gap={0.8}>
						<Wallet size={theme.iconSize.sm} color={theme.palette.blue[500]} />
						<Typography variant="body2" color="text.secondary">
							{t(R.balance)}
						</Typography>
					</Stack>
					<Stack direction="row" alignItems="center" gap={0.8}>
						<Box
							px={0.8}
							py={0.2}
							bgcolor={health.bg}
							borderRadius={theme.shape.pillRadius}
						>
							<Typography variant="button" color={health.color}>
								{health.label}
							</Typography>
						</Box>
						<Typography
							variant="value"
							color={
								positive ? theme.palette.green[600] : theme.palette.red[600]
							}
							tabular
						>
							{signedNumberFormatter.format(balance)}{" "}
							<Typography
								component="span"
								variant="caption"
								color="text.secondary"
							>
								{currencySign}
							</Typography>
						</Typography>
					</Stack>
				</Stack>
			</Box>
			<Stack
				direction="row"
				gap={1}
				p={1.2}
				bgcolor={theme.palette.blue[50]}
				borderRadius={theme.radii.md}
			>
				<Lightbulb
					size={theme.iconSize.sm}
					color={theme.palette.blue[500]}
					style={{ flexShrink: 0, marginTop: 2 }}
				/>
				<Stack flex={1} minWidth={0} textAlign="left">
					<Typography
						variant="caption"
						color={theme.palette.grey[700]}
						lineHeight={1.6}
					>
						{tipText}
					</Typography>
					<Typography
						variant="button"
						color={tipCtaColor}
						mt={0.6}
						display="block"
					>
						{tipCta}
					</Typography>
				</Stack>
			</Stack>
		</Stack>
	);
};
